import { HttpClient } from '@angular/common/http';
import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ColDef, GridReadyEvent } from '@ag-grid-community/core';
import { firstValueFrom } from 'rxjs';

import { AgFns, GridOptionsExt, ISortModel, UtilDialogService } from '@core';
import { environment } from '@env';
import { ProgramReview, ProgramReviewFinding, ReviewResolutionEnum } from '@models';
import { ToastrService } from 'ngx-toastr';

import { ProgramFrmComponent } from './program-frm.component';
import { AccountDbQueryService } from '../services/account-db-query.service';

/** Mirrors ProgramCheckRunDto in ProgramCheckService.cs. PascalCase on the wire. */
interface ProgramCheckRunDto {
  ProgramReviewId: string;
  Summary: string;
  FindingCount: number;
  OpenCount: number;
}

/** The part of ProgramCheckStatusDto this panel reads. PascalCase on the wire. */
interface ProgramCheckStatus {
  HasReview: boolean;
  IsCurrent: boolean;
  Problem?: string;
}

const RESOLUTION_NAMES: Record<number, string> = {
  [ReviewResolutionEnum.Open]: 'Open',
  [ReviewResolutionEnum.Fixed]: 'Fixed',
  [ReviewResolutionEnum.Accepted]: 'Accepted',
  [ReviewResolutionEnum.Dismissed]: 'Dismissed',
};

/**
 * AI Program Check panel of the Program form - AI Program Check in the program menu. GlobalAdmins only.
 *
 * Run AI Program Check has the AI apply the rules in effect (master, supplier, account and program
 * overrides) to each configuration of THIS program only, and saves the findings. The panel shows the
 * latest check. A person resolves each finding: Accepted (intended) or Dismissed (the AI was wrong).
 * Fixed comes only from a new check, after the configuration is changed. The next check keeps Accepted
 * and Dismissed findings that come back unchanged. The Program form's Save saves the resolutions.
 */
@Component({
  selector: 'prox-program-check-sub',
  templateUrl: './program-check.sub.component.html',
  standalone: false,
})
export class ProgramCheckSubComponent implements OnInit, OnChanges {
  @Input() parent!: ProgramFrmComponent;
  @Input() visible!: boolean;

  private http = inject(HttpClient);
  private toastr = inject(ToastrService);

  review?: ProgramReview;
  findings: ProgramReviewFinding[] = [];
  gridOptions: GridOptionsExt = {};
  private isLoaded = false;

  isRunning = false;
  runElapsed = 0;
  runError?: string;
  /** Set when the latest check is out of date: the catalog or the AI rules changed after it. */
  staleText?: string;

  constructor(
    public dbQueryService: AccountDbQueryService,
    public dialogService: UtilDialogService,
  ) {
    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      getRowId: (params) => params.data.id,
    });
  }

  get program() {
    return this.parent.program;
  }

  ngOnInit() {
    this.refresh();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.refresh();
  }

  async refresh(forceRefresh = false) {
    if (!this.visible && !forceRefresh) return;
    this.parent.updateComponentNameMap(this);
    if (this.isLoaded && !forceRefresh) return;
    if (this.parent.isBeingAdded) {
      // A new program (or a new cycle) has no check yet.
      this.review = undefined;
      this.findings = [];
      this.isLoaded = false;
      AgFns.refreshGrid(this.gridOptions, this.findings);
      return;
    }
    await this.parent.wrapBusy(async () => {
      await this.loadReview();
      this.isLoaded = true;
    });
  }

  private async loadReview() {
    this.review = await this.dbQueryService.getLatestProgramReview(this.program.id);
    this.findings = [...(this.review?.programReviewFindings ?? [])];
    AgFns.refreshGrid(this.gridOptions, this.findings);
    await this.loadStaleText();
  }

  /** The same status the activation gate reads. If it cannot be read, the panel says nothing. */
  private async loadStaleText() {
    this.staleText = undefined;
    try {
      const status = await firstValueFrom(
        this.http.get<ProgramCheckStatus>(`${environment.apiUrl}ProgramReview/ProgramCheckStatus?programId=${this.program.id}`),
      );
      if (status?.HasReview && !status.IsCurrent) {
        this.staleText = `This check is out of date. ${status.Problem ?? ''} Run the AI Program Check again.`;
      }
    } catch {
      // The findings still show; only the note is missing.
    }
  }

  onGridReady(event: GridReadyEvent) {
    // Long text wraps. The theme sets a cell's line-height to the row height, so the text sits in its
    // own element with a normal line-height; textContent, never HTML, because the text is AI output.
    const wrapped = {
      wrapText: true,
      autoHeight: true,
      cellRenderer: (p: { value?: string }) => {
        const el = document.createElement('div');
        el.className = 'whitespace-normal leading-snug py-2';
        el.textContent = p.value ?? '';
        return el;
      },
    };
    const editable = (f?: ProgramReviewFinding) => !!f && !this.isReadOnly() && f.reviewResolutionId !== ReviewResolutionEnum.Fixed;
    const colDefs: ColDef<ProgramReviewFinding>[] = [
      {
        headerName: 'Configuration',
        colId: 'config',
        valueGetter: (p) => p.data?.productTypeConfig?.name ?? '',
        filter: 'agTextColumnFilter',
        ...wrapped,
        ...AgFns.flexWidth(130),
      },
      {
        headerName: 'Manufacturer / Product Type',
        colId: 'style',
        valueGetter: (p) => {
          const pt = p.data?.productType;
          return pt ? [pt.manufacturer?.name, pt.productKey, pt.name].filter((x) => !!x).join(' / ') : '';
        },
        filter: 'agTextColumnFilter',
        ...wrapped,
        ...AgFns.flexWidth(220),
      },
      {
        headerName: 'Rule',
        colId: 'rule',
        valueGetter: (p) => (p.data ? (p.data.guideline?.title ?? (p.data.guidelineId ? 'Rule' : 'Code check')) : ''),
        filter: 'agSetColumnFilter',
        ...wrapped,
        ...AgFns.flexWidth(140),
      },
      {
        headerName: 'Severity',
        colId: 'severity',
        valueGetter: (p) => (p.data ? (p.data.isBlocking ? 'Block' : 'Warn') : ''),
        headerTooltip: 'Block: in Enforce mode, an open finding stops activation. Warn: it never does; a reason is optional.',
        filter: 'agSetColumnFilter',
        ...AgFns.fixWidth(90),
      },
      { headerName: 'Finding', field: 'message', ...wrapped, ...AgFns.flexWidth(400) },
      {
        headerName: 'Resolution',
        colId: 'resolution',
        editable: (p) => editable(p.data),
        valueGetter: (p) => (p.data ? RESOLUTION_NAMES[p.data.reviewResolutionId] : ''),
        valueSetter: (p) => {
          const id = Number(Object.keys(RESOLUTION_NAMES).find((k) => RESOLUTION_NAMES[Number(k)] === p.newValue));
          if (!p.data || !id || id === ReviewResolutionEnum.Fixed) return false;
          p.data.reviewResolutionId = id as ReviewResolutionEnum;
          return true;
        },
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: { values: ['Open', 'Accepted', 'Dismissed'] },
        cellClassRules: {
          'red_pointer': (p) => p.data?.reviewResolutionId === ReviewResolutionEnum.Open && !!p.data?.isBlocking,
          'green_pointer': (p) => p.data?.reviewResolutionId === ReviewResolutionEnum.Fixed,
        },
        headerTooltip: 'Accepted: it is intended. Dismissed: the AI was wrong. A Block finding needs a reason. Fixed comes from a new check.',
        filter: 'agSetColumnFilter',
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'Reason',
        field: 'resolutionReason',
        editable: (p) => editable(p.data),
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
        cellEditorParams: { maxLength: 2000, rows: 6, cols: 60 },
        ...wrapped,
        ...AgFns.flexWidth(220),
      },
      {
        headerName: 'Resolved By',
        colId: 'resolvedBy',
        valueGetter: (p) => p.data?.resolvedByUser?.fullName ?? '',
        tooltipValueGetter: (p) => (p.data?.resolvedTs ? new Date(p.data.resolvedTs).toLocaleString() : ''),
        ...AgFns.flexWidth(120),
      },
    ];
    const sortModel: ISortModel = [
      { colId: 'config', sort: 'asc' },
      { colId: 'rule', sort: 'asc' },
    ];
    AgFns.initGrid(event, colDefs, sortModel);
  }

  /** Runs the check on the SAVED program and findings of this program only, then shows the new check. */
  async onRunCheck() {
    if (this.parent.hasChanges()) {
      await this.dialogService.okDialog('Save first', 'Save or undo your changes to the program, then run the AI Program Check.');
      return;
    }
    this.runError = undefined;
    this.isRunning = true;
    this.runElapsed = 0;
    const timer = setInterval(() => this.runElapsed++, 1000);
    try {
      const result = await firstValueFrom(
        this.http.post<ProgramCheckRunDto>(`${environment.apiUrl}ProgramReview/RunProgramCheck?programId=${this.program.id}`, {}),
      );
      await this.loadReview();
      this.toastr.success(result.Summary, 'AI Program Check');
    } catch (err: any) {
      this.runError = err?.error?.Message ?? err?.message ?? String(err);
    } finally {
      clearInterval(timer);
      this.isRunning = false;
    }
  }

  /** Open BLOCKING findings: these stop activation in Enforce mode. */
  openCount() {
    return this.findings.filter((f) => f.reviewResolutionId === ReviewResolutionEnum.Open && f.isBlocking).length;
  }

  /** Open warnings: shown, never a reason to refuse activation. */
  warningCount() {
    return this.findings.filter((f) => f.reviewResolutionId === ReviewResolutionEnum.Open && !f.isBlocking).length;
  }

  runText() {
    if (!this.review) return 'This program has no AI Program Check yet.';
    const by = this.review.runByUser?.fullName;
    return `Last check ${new Date(this.review.runTs).toLocaleString()}${by ? ' by ' + by : ''}. ${this.review.summary ?? ''}`;
  }

  /** Called by the Program form before it saves. False = do not save. */
  async beforeSave(): Promise<boolean> {
    const problems = this.findings
      .filter((f) => !f.entityAspect.entityState.isUnchanged())
      // A warning may be resolved without a reason; a blocking finding may not.
      .filter((f) => f.isBlocking && f.reviewResolutionId !== ReviewResolutionEnum.Open && !(f.resolutionReason ?? '').trim())
      .map((f) => `${f.productTypeConfig?.name ?? ''}: ${RESOLUTION_NAMES[f.reviewResolutionId]} needs a reason.`);
    if (problems.length > 0) {
      await this.dialogService.okDialog('Cannot save the AI Program Check', [...new Set(problems)].join('<br/>'));
      return false;
    }
    return true;
  }

  isReadOnly() {
    return this.parent.isReadOnly() || !this.parent.authUser?.isGlobalAdmin;
  }
}
